import {faCircleInfo} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Flex, Form, FormInstance, Input, Select, Tooltip} from 'antd';
import {useEffect} from 'react';

import {spacer_xs} from '../../../../../css/var-export.scss';
import {Locale} from '../../../../../provider/locale/localization';
import type {LangKeyType} from '../../../../../provider/locale/translation/type';
import {useCompany} from '../../../../../service/company/company-hook';
import {useFormRules} from '../../../../../service/form/form-rules-hook';
import {useReviewCustomBrands} from '../../../../../service/reviews/reviews-custom-brands';
import {CompanySelect} from '../../company-select/company-select';

import {
    ImportReviewsEntityScopeEnum,
    ImportReviewsFormFieldNamesEnum,
    ImportReviewsFormFieldsType,
} from './import-reviews-form-type';
import * as styles from './import-reviews-brand-company-scope-card.module.scss';

type PropsType = {
    form: FormInstance<ImportReviewsFormFieldsType>;
};

export function ImportReviewsBrandCompanyScopeCard(props: PropsType): JSX.Element {
    const {form} = props;

    const {requiredFieldRule} = useFormRules();
    const {data: brands, isFetching: isBrandsLoading} = useReviewCustomBrands();

    const scope =
        Form.useWatch(ImportReviewsFormFieldNamesEnum.entityScope, form) ?? ImportReviewsEntityScopeEnum.brand;

    const isCompanyScope = scope === ImportReviewsEntityScopeEnum.company;

    const selectedCompanyId = Form.useWatch(ImportReviewsFormFieldNamesEnum.company, form) ?? null;

    const {result: selectedCompany} = useCompany(selectedCompanyId || null);

    useEffect(() => {
        if (selectedCompany) {
            form.setFieldValue(ImportReviewsFormFieldNamesEnum.brand, selectedCompany.brand.id);
        }
    }, [form, selectedCompany]);

    function handleScopeChange(nextScope: ImportReviewsEntityScopeEnum): void {
        form.setFieldValue(ImportReviewsFormFieldNamesEnum.entityScope, nextScope);

        form.resetFields([ImportReviewsFormFieldNamesEnum.brand, ImportReviewsFormFieldNamesEnum.company]);
    }

    useEffect(() => {
        const isBrandScope = scope === ImportReviewsEntityScopeEnum.brand;

        if (!isBrandScope || !brands || brands.length !== 1) {
            return;
        }

        const [firstBrand] = brands;

        form.setFieldsValue({
            [ImportReviewsFormFieldNamesEnum.brand]: firstBrand?.id,
        });
    }, [brands, form, scope]);

    function renderScopeTab(nextScope: ImportReviewsEntityScopeEnum, labelKey: LangKeyType): JSX.Element {
        const isActive = scope === nextScope;

        return (
            <button
                className={`${styles.ImportReviewsBrandCompanyScopeCard_tab} ${
                    isActive ? styles.ImportReviewsBrandCompanyScopeCard_tabActive : ''
                }`}
                onClick={() => handleScopeChange(nextScope)}
                type="button"
            >
                <Text inheritColor stringKey={labelKey} />
            </button>
        );
    }

    return (
        <>
            <Form.Item hidden name={ImportReviewsFormFieldNamesEnum.entityScope}>
                <Input />
            </Form.Item>

            {isCompanyScope && (
                <Form.Item<ImportReviewsFormFieldsType> hidden name={ImportReviewsFormFieldNamesEnum.brand} preserve>
                    <Input />
                </Form.Item>
            )}

            <Form.Item<ImportReviewsFormFieldsType>
                className={styles.ImportReviewsBrandCompanyScopeCard_formItemRoot}
                colon={false}
                label={
                    <Flex align="center" gap={spacer_xs} wrap="wrap">
                        <Text large lighter stringKey="REVIEW_IMPORT__REVIEW_TYPE_LABEL" />
                        <Tooltip title={<Locale stringKey="REVIEW_IMPORT__REVIEW_TYPE_TOOLTIP" />}>
                            <span className={styles.ImportReviewsBrandCompanyScopeCard_infoIconWrap}>
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                        </Tooltip>
                    </Flex>
                }
                required
            >
                <div className={styles.ImportReviewsBrandCompanyScopeCard_panel}>
                    <div className={styles.ImportReviewsBrandCompanyScopeCard_tabBar} role="tablist">
                        {renderScopeTab(ImportReviewsEntityScopeEnum.brand, 'REVIEW__FORM__FIELD_BRAND')}
                        {renderScopeTab(ImportReviewsEntityScopeEnum.company, 'REVIEW__FORM__FIELD_COMPANY')}
                    </div>
                    <div className={styles.ImportReviewsBrandCompanyScopeCard_selectArea}>
                        {scope === ImportReviewsEntityScopeEnum.brand ? (
                            <div className={styles.ImportReviewsBrandCompanyScopeCard_nestedItem}>
                                <Form.Item<ImportReviewsFormFieldsType>
                                    name={ImportReviewsFormFieldNamesEnum.brand}
                                    noStyle
                                    preserve={false}
                                    rules={[requiredFieldRule]}
                                >
                                    <Select<number>
                                        disabled={isBrandsLoading}
                                        loading={isBrandsLoading}
                                        placeholder={
                                            <Text large lightest stringKey="REVIEW__FORM__FIELD_BRAND__PLACEHOLDER" />
                                        }
                                        size="large"
                                    >
                                        {brands?.map((brand) => (
                                            <Select.Option key={brand.id} label={brand.name} value={brand.id}>
                                                <Text large lighter>
                                                    {brand.name}
                                                </Text>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </div>
                        ) : (
                            <div className={styles.ImportReviewsBrandCompanyScopeCard_nestedItem}>
                                <Form.Item<ImportReviewsFormFieldsType>
                                    name={ImportReviewsFormFieldNamesEnum.company}
                                    noStyle
                                    preserve={false}
                                    rules={[requiredFieldRule]}
                                >
                                    <CompanySelect
                                        brandId={null}
                                        getPopupContainer={(triggerNode) => triggerNode.ownerDocument.body}
                                        size="large"
                                    />
                                </Form.Item>
                            </div>
                        )}
                    </div>
                </div>
            </Form.Item>
        </>
    );
}
